// context/AuthContext.js
// Propósito: Gestionar sesión (usuario + token), persistir en localStorage y exponer acciones.

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { login as apiLogin, register as apiRegister, getMe as apiGetMe } from '../services/api.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Restore session from localStorage
  useEffect(() => {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem('auth_token') : null;
    if (!saved) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const me = await apiGetMe(saved);
        setToken(saved);
        setUser(me.user);
      } catch (e) {
        // token invalid — clear
        window.localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function login(email, password) {
    setError(null);
    const res = await apiLogin(email, password);
    setUser(res.user);
    setToken(res.token);
    window.localStorage.setItem('auth_token', res.token);
    return res.user;
  }

  async function register(name, email, password) {
    setError(null);
    const res = await apiRegister(name, email, password);
    setUser(res.user);
    setToken(res.token);
    window.localStorage.setItem('auth_token', res.token);
    return res.user;
  }

  function logout() {
    setUser(null);
    setToken(null);
    window.localStorage.removeItem('auth_token');
  }

  async function loginWithGoogle(oauthToken) {
    // token from backend callback
    setToken(oauthToken);
    window.localStorage.setItem('auth_token', oauthToken);
    const me = await apiGetMe(oauthToken);
    setUser(me.user);
    return me.user;
  }

  // Actualiza parcialmente el usuario en memoria (p.ej. tras editar perfil)
  function updateUser(partial) {
    setUser((prev) => (prev ? { ...prev, ...partial } : prev));
  }

  async function refreshUser() {
    if (!token) return null;
    const me = await apiGetMe(token);
    setUser(me.user);
    return me.user;
  }

  const value = useMemo(() => ({ user, token, loading, error, login, register, loginWithGoogle, logout, updateUser, refreshUser }), [user, token, loading, error]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
